import { useState, useRef, useEffect, useMemo } from 'react';
import { Button, SkeletonBox } from '@/components';
import { useMobileScrollLock } from '@/common/hooks';
import { MessageBubble } from './MessageBubble';
import { MediaViewerModal } from '@/common/components/MediaViewer/MediaViewerModal';
import toast from 'react-hot-toast';
import { LazyEmojiPicker } from './LazyEmojiPicker';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';
import WhatsappChatImage from '../../assets/Whatsapp_chat.png';
import instagramIcon from '../../assets/Instagram_icon.png';
import AttachmentClip from '../../assets/Attachement_clip.png';
import SendMsgIcon from '../../assets/Send_msg.png';
import { formatWindowExpiry, getAvatarInitial } from '@/common/utils';
import { resolveWhatsAppMediaUrl } from '../utils/mediaUrl';
import { serializeComposer } from '../utils/richMessage';
import {
  conversationDraftKey,
  draftToEditorHtml,
  loadConversationDraft,
  saveConversationDraft,
} from '../utils/conversationDrafts';

import OpusMediaRecorder from 'opus-media-recorder';
import encoderWorkerPath from 'opus-media-recorder/encoderWorker.umd.js?url';
import OggOpusEncoderWasmPath from 'opus-media-recorder/OggOpusEncoder.wasm?url';
import WebMOpusEncoderWasmPath from 'opus-media-recorder/WebMOpusEncoder.wasm?url';

export function ChatArea({
  activeConversation,
  messages,
  isLoading,
  onSendMessage,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isWindowActive = true,
  windowExpiresAt,
  onMobileBack,
  isSidebarOpen,
  onToggleSidebar,
  onReactToMessage,
  canSend,
  canReact,
  platform = 'whatsapp',
}) {
  const [hasDraft, setHasDraft] = useState(false);
  const [showFormatBar, setShowFormatBar] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioStream, setAudioStream] = useState(null);
  const [isMediaViewerOpen, setIsMediaViewerOpen] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [scopedViewerMedia, setScopedViewerMedia] = useState(null);

  const [keyboardInset, setKeyboardInset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const { can } = usePermissions();
  const canSendMessages =
    typeof canSend === 'boolean' ? canSend : can(PERMISSIONS.WA_CHATS.CREATE);
  const isInstagram = platform === 'instagram';
  const canReactMessages =
    !isInstagram &&
    (typeof canReact === 'boolean' ? canReact : canSendMessages);
  const expiredWindowLabel = formatWindowExpiry(windowExpiresAt);

  const emojiPickerRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    const sync = () => {
      const inset = Math.max(
        0,
        window.innerHeight - viewport.height - viewport.offsetTop,
      );
      setKeyboardInset(inset > 80 ? inset : 0);
    };
    sync();
    viewport.addEventListener('resize', sync);
    viewport.addEventListener('scroll', sync);
    return () => {
      viewport.removeEventListener('resize', sync);
      viewport.removeEventListener('scroll', sync);
    };
  }, []);
  const messageInputRef = useRef(null);
  const draftKeyRef = useRef(null);

  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const attachmentMenuRef = useRef(null);

  useMobileScrollLock(showEmojiPicker || showAttachmentMenu);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(e.target)
      ) {
        setShowEmojiPicker(false);
      }
      if (
        attachmentMenuRef.current &&
        !attachmentMenuRef.current.contains(e.target)
      ) {
        setShowAttachmentMenu(false);
      }
    };
    if (showEmojiPicker || showAttachmentMenu) {
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('touchstart', handleClickOutside, true);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('touchstart', handleClickOutside, true);
    };
  }, [showEmojiPicker, showAttachmentMenu]);

  const triggerFileInput = (acceptTypes) => {
    if (fileInputRef.current) {
      fileInputRef.current.accept = acceptTypes;
      fileInputRef.current.click();
    }
    setShowAttachmentMenu(false);
  };

  const persistDraft = () => {
    const key = draftKeyRef.current;
    const el = messageInputRef.current;
    if (!key || !el) return;
    saveConversationDraft(key, serializeComposer(el));
  };

  const syncDraft = () => {
    const el = messageInputRef.current;
    setHasDraft(Boolean(el?.textContent?.replace(/\u00a0/g, ' ').trim()));
    resizeMessageInput();
    persistDraft();
  };

  const handleEmojiSelect = (emoji) => {
    const el = messageInputRef.current;
    if (!el) return;
    el.focus();
    document.execCommand('insertText', false, emoji);
    syncDraft();
  };

  useEffect(() => {
    let timer;
    if (isRecording) {
      timer = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
    }
    return () => clearInterval(timer);
  }, [isRecording]);

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  function audioBufferToWav(buffer) {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;
    const numSamples = buffer.length;
    const blockAlign = (numChannels * bitDepth) / 8;
    const byteRate = sampleRate * blockAlign;
    const dataSize = numSamples * blockAlign;
    const bufferSize = 44 + dataSize;

    const arrayBuffer = new ArrayBuffer(bufferSize);
    const view = new DataView(arrayBuffer);

    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, format, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);

    let offset = 44;
    for (let i = 0; i < numSamples; i++) {
      for (let channel = 0; channel < numChannels; channel++) {
        let sample = buffer.getChannelData(channel)[i];
        sample = Math.max(-1, Math.min(1, sample));
        sample = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
        view.setInt16(offset, sample, true);
        offset += 2;
      }
    }

    return new Blob([arrayBuffer], { type: 'audio/wav' });
  }

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioStream(stream);

      const workerOptions = {
        encoderWorkerFactory: function () {
          return new Worker(encoderWorkerPath);
        },
        OggOpusEncoderWasmPath: OggOpusEncoderWasmPath,
        WebMOpusEncoderWasmPath: WebMOpusEncoderWasmPath,
      };

      const options = { mimeType: 'audio/ogg' };
      const recorder = new OpusMediaRecorder(stream, options, workerOptions);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = async () => {
        if (chunks.length > 0) {
          const rawBlob = new Blob(chunks, { type: 'audio/ogg' });

          if (platform === 'instagram') {
            try {
              const audioCtx = new (
                window.AudioContext || window.webkitAudioContext
              )();
              const arrayBuffer = await rawBlob.arrayBuffer();
              const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
              const wavBlob = audioBufferToWav(audioBuffer);
              const file = new File([wavBlob], 'voice-message.wav', {
                type: 'audio/wav',
              });
              if (onSendMessage) {
                onSendMessage('', file);
              }
              return;
            } catch (err) {
              console.error('Failed to convert recorded audio to WAV:', err);
            }
          }

          const file = new File([rawBlob], 'voice-message.ogg', {
            type: 'audio/ogg',
          });
          if (onSendMessage) {
            onSendMessage('', file);
          }
        }
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (err) {
      console.error('Error starting audio recording:', err);
      toast.error(
        'Could not access microphone. Please enable it in browser settings.',
      );
    }
  };

  const stopAndSendRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
      if (audioStream) {
        audioStream.getTracks().forEach((track) => track.stop());
      }
      setIsRecording(false);
      setMediaRecorder(null);
      setAudioStream(null);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.ondataavailable = null;
      mediaRecorder.stop();
      if (audioStream) {
        audioStream.getTracks().forEach((track) => track.stop());
      }
      setIsRecording(false);
      setMediaRecorder(null);
      setAudioStream(null);
      toast('Recording discarded');
    }
  };

  const processFile = (file) => {
    if (!file) return;
    const fileExt = file.name
      .substring(file.name.lastIndexOf('.'))
      .toLowerCase();
    const blockedExtensions = [
      '.zip',
      '.rar',
      '.7z',
      '.tar',
      '.gz',
      '.dmg',
      '.iso',
    ];

    if (blockedExtensions.includes(fileExt)) {
      toast.error('Zip and archive files are not allowed');
      return;
    }

    const isVideo =
      file.type?.startsWith('video/') ||
      ['.mp4', '.3gp', '.mov', '.avi', '.mkv', '.webm'].includes(fileExt);

    if (isVideo && file.size > 16 * 1024 * 1024) {
      toast.error('Video size exceeds 16MB limit');
      return;
    }

    if (file.size > 64 * 1024 * 1024) {
      toast.error('File size exceeds 64MB limit');
      return;
    }

    if (onSendMessage) {
      onSendMessage('', file);
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      Array.from(files).forEach((file) => processFile(file));
      e.target.value = '';
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!activeConversation || !isWindowActive) return;
    dragCounter.current += 1;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!activeConversation || !isWindowActive) return;
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!activeConversation || !isWindowActive) return;
    setIsDragging(false);
    dragCounter.current = 0;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      Array.from(files).forEach((file) => processFile(file));
    }
  };

  useEffect(() => {
    return () => {
      if (audioStream) {
        audioStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [audioStream]);

  const [showNewMessageAlert, setShowNewMessageAlert] = useState(false);
  const messagesEndRef = useRef(null);

  // Compute a flattened list of all media, extracting carousel cards into separate entries
  const allMedia = useMemo(() => {
    return (messages || [])
      .flatMap((g) => g.messages || [])
      .flatMap((m) => {
        if (m.messageType === 'template' && Array.isArray(m.template?.cards)) {
          return m.template.cards
            .map((card, idx) => {
              const file = m.attachments?.[idx]?.file;
              if (!file) return null;
              return {
                ...m,
                id: `${m.id}-card-${idx}`,
                mediaUrl: resolveWhatsAppMediaUrl(file),
                mimeType: m.attachments[idx].mime_type || 'image/jpeg',
                fileName: m.attachments[idx].filename,
              };
            })
            .filter(Boolean);
        }
        return m.mediaUrl ? [m] : [];
      });
  }, [messages]);

  const observerTargetRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const prevScrollHeightRef = useRef(0);
  const isUserScrolledUpRef = useRef(false);

  // Track if we have completed the initial scroll for the current conversation
  const hasInitialScrolledRef = useRef(false);

  // Reset initial scroll flag when active conversation changes
  useEffect(() => {
    hasInitialScrolledRef.current = false;
    prevScrollHeightRef.current = 0;
    isUserScrolledUpRef.current = false;
  }, [activeConversation?.id]);

  // Track total number of messages to detect new incoming messages
  const totalMessagesCount =
    messages?.reduce((acc, group) => acc + (group.messages?.length || 0), 0) ||
    0;
  const prevMessagesCountRef = useRef(totalMessagesCount);

  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
    setShowNewMessageAlert(false);
  };

  // 1. Initial Load & Pagination scroll maintainer
  useEffect(() => {
    if (prevScrollHeightRef.current > 0 && scrollContainerRef.current) {
      // We just fetched older messages (pagination)
      const newScrollHeight = scrollContainerRef.current.scrollHeight;
      scrollContainerRef.current.scrollTop =
        newScrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = 0;
    } else if (
      !hasInitialScrolledRef.current &&
      totalMessagesCount > 0 &&
      !isLoading
    ) {
      // Initial load of messages, jump to bottom instantly
      hasInitialScrolledRef.current = true;

      // Use requestAnimationFrame to ensure DOM is fully rendered before scrolling
      requestAnimationFrame(() => {
        scrollToBottom('auto');

        // Add a small timeout to account for any images/media that might still be computing their layout height
        setTimeout(() => {
          if (!isUserScrolledUpRef.current) {
            scrollToBottom('auto');
          }
        }, 150);

        // One more safety net for slower media loads
        setTimeout(() => {
          if (!isUserScrolledUpRef.current) {
            scrollToBottom('auto');
          }
        }, 500);
      });
    }
  }, [messages, isLoading, totalMessagesCount]);

  // 2. New Message Arrival Handling
  useEffect(() => {
    // Only trigger if this is NOT the initial load (which is handled above)
    if (
      hasInitialScrolledRef.current &&
      totalMessagesCount > prevMessagesCountRef.current &&
      prevMessagesCountRef.current > 0
    ) {
      // New messages arrived (either we sent one, or received one)
      if (isUserScrolledUpRef.current) {
        // User is reading old messages, don't interrupt them
        setShowNewMessageAlert(true);
      } else {
        // User is at bottom, smoothly scroll to the new message
        scrollToBottom('smooth');
      }
    }
    prevMessagesCountRef.current = totalMessagesCount;
  }, [totalMessagesCount]);

  // 3. Intersection Observer for Fetching Older Messages
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasNextPage &&
          !isFetchingNextPage &&
          scrollContainerRef.current
        ) {
          // Only trigger if we actually have scrollable content
          if (
            scrollContainerRef.current.scrollHeight >
            scrollContainerRef.current.clientHeight
          ) {
            prevScrollHeightRef.current =
              scrollContainerRef.current.scrollHeight;
            fetchNextPage();
          }
        }
      },
      { threshold: 0.1 },
    );

    const target = observerTargetRef.current;
    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Check if user is near the bottom (within 100px)
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

    isUserScrolledUpRef.current = !isNearBottom;

    if (isNearBottom && showNewMessageAlert) {
      setShowNewMessageAlert(false);
    }
  };

  const MESSAGE_INPUT_MAX_HEIGHT = 128;

  const resizeMessageInput = () => {
    const el = messageInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, MESSAGE_INPUT_MAX_HEIGHT);
    el.style.height = `${next}px`;
    el.style.overflowY =
      el.scrollHeight > MESSAGE_INPUT_MAX_HEIGHT ? 'auto' : 'hidden';
  };

  const draftKey = conversationDraftKey(platform, activeConversation?.id);

  useEffect(() => {
    const el = messageInputRef.current;
    const previousKey = draftKeyRef.current;
    if (el && previousKey && previousKey !== draftKey) {
      saveConversationDraft(previousKey, serializeComposer(el));
    }
    draftKeyRef.current = draftKey;
    if (!el) return;
    const saved = loadConversationDraft(draftKey);
    el.innerHTML = draftToEditorHtml(saved);
    setHasDraft(Boolean(el.textContent?.replace(/\u00a0/g, ' ').trim()));
    setShowFormatBar(false);
    resizeMessageInput();
    return () => {
      const key = draftKeyRef.current;
      if (el && key) saveConversationDraft(key, serializeComposer(el));
    };
  }, [draftKey]);

  useEffect(() => {
    const onSelectionChange = () => {
      const el = messageInputRef.current;
      const selection = window.getSelection();
      if (
        !el ||
        !selection ||
        selection.rangeCount === 0 ||
        selection.isCollapsed
      ) {
        setShowFormatBar(false);
        return;
      }
      const node = selection.anchorNode;
      setShowFormatBar(Boolean(node && el.contains(node)));
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () =>
      document.removeEventListener('selectionchange', onSelectionChange);
  }, []);

  const handleSend = (e) => {
    e.preventDefault();
    if (!isWindowActive) return; // blocked at page level with toast
    const el = messageInputRef.current;
    if (!el || !onSendMessage) return;
    const text = serializeComposer(el);
    const visible = text
      .replace(/<[^>]+>/g, '')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .trim();
    if (!visible) return;
    onSendMessage(text);
    el.innerHTML = '';
    saveConversationDraft(draftKeyRef.current, '');
    setHasDraft(false);
    setShowFormatBar(false);
    resizeMessageInput();
  };

  const applyFormat = (command) => {
    const el = messageInputRef.current;
    if (!el) return;
    el.focus();
    document.execCommand(command, false);
    syncDraft();
  };

  const handleMessageKeyDown = (e) => {
    const mod = e.ctrlKey || e.metaKey;
    if (mod && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'b') {
      e.preventDefault();
      applyFormat('bold');
      return;
    }
    if (mod && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'i') {
      e.preventDefault();
      applyFormat('italic');
      return;
    }
    if (mod && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'u') {
      e.preventDefault();
      applyFormat('underline');
      return;
    }
    if (mod && e.shiftKey && e.key.toLowerCase() === 'x') {
      e.preventDefault();
      applyFormat('strikeThrough');
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const handleComposerPaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData?.getData('text/plain') || '';
    if (!text) return;
    const lines = text.split(/\r\n|\n|\r/);
    lines.forEach((line, index) => {
      if (index > 0) document.execCommand('insertLineBreak');
      if (line) document.execCommand('insertText', false, line);
    });
    syncDraft();
  };

  if (!activeConversation) {
    return (
      <div className="flex flex-col items-center justify-center h-full w-full bg-layer-1-background text-text-muted p-4 relative">
        {/* Tablet Toggle Sidebar Button (when sidebar is closed) */}
        {!isSidebarOpen && onToggleSidebar && (
          <button
            onClick={onToggleSidebar}
            className="absolute top-4 left-4 hidden md:block xl:hidden p-2 text-text-muted hover:text-text-primary rounded-button hover:bg-layer-7-hover transition-colors border border-border-default bg-layer-2-sidebar"
            title="Show Sidebar"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <rect
                x="3"
                y="3"
                width="18"
                height="18"
                rx="2"
                ry="2"
                strokeWidth={2}
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 3v18"
              />
            </svg>
          </button>
        )}
        <div className="w-[60px] h-[60px] mb-4 flex items-center justify-center shrink-0">
          {platform === 'instagram' ? (
            <div className="w-full h-full rounded-[14px] bg-gradient-to-tr from-amber-500/10 via-rose-500/10 to-purple-600/10 border border-rose-200/40 flex items-center justify-center">
              <img
                src={instagramIcon}
                alt="Instagram Chat"
                className="w-7 h-7 object-contain"
              />
            </div>
          ) : (
            <img
              src={WhatsappChatImage}
              alt="WhatsApp Chat"
              className="w-full h-full object-contain"
            />
          )}
        </div>
        <h3 className="text-2xl font-medium leading-tight text-center text-text-primary mb-2 font-sf-pro-display">
          {platform === 'instagram'
            ? 'Instagram Direct Messages'
            : 'WhatsApp Business Chat'}
        </h3>
        <p className="text-sm leading-normal max-w-md text-center mb-6 text-text-muted">
          {platform === 'instagram'
            ? 'Select a conversation from the sidebar to view messages or start a new chat.'
            : 'Select a conversation from the sidebar to view messages or start a new chat.'}
        </p>
        {!isSidebarOpen && onToggleSidebar && (
          <Button
            variant="primary"
            onClick={onToggleSidebar}
            className="hidden md:flex xl:hidden items-center gap-2 px-4 py-2 rounded-full shadow-lg h-auto"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <rect
                x="3"
                y="3"
                width="18"
                height="18"
                rx="2"
                ry="2"
                strokeWidth={2}
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 3v18"
              />
            </svg>
            Open Chats Sidebar
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col h-full min-h-0 min-w-0 w-full overflow-hidden bg-layer-1-background relative ${
        platform === 'whatsapp' ? 'wa-inbox-chat' : ''
      }`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag & Drop Overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-primary/10 border-4 border-dashed border-primary rounded-3xl m-2 backdrop-blur-[2px]">
          <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl animate-bounce">
            <svg
              className="w-16 h-16 text-primary mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <h3 className="text-xl font-bold text-gray-800">
              Drop files to send
            </h3>
            <p className="text-sm text-gray-500 mt-2">
              Images, Videos, PDFs, and Documents supported
            </p>
          </div>
        </div>
      )}

      {/* Sticky Header */}
      <div className="wa-chat-header shrink-0 z-10 flex items-center justify-between p-2 sm:p-4 bg-layer-2-sidebar border-b border-border-default min-h-[64px] gap-2 min-w-0">
        <div className="flex items-center min-w-0 flex-1">
          {/* Mobile/Tablet/Laptop Back Button */}
          {onMobileBack && (
            <button
              onClick={onMobileBack}
              className="mr-1 sm:mr-2 p-1.5 text-text-muted hover:text-text-primary rounded-button hover:bg-layer-7-hover transition-colors shrink-0"
              title="Back to conversations"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          )}

          <div className="relative mr-3 sm:mr-4 shrink-0">
            {activeConversation.avatarUrl ? (
              <img
                src={activeConversation.avatarUrl}
                alt={
                  activeConversation.contactName ||
                  activeConversation.contactPhone
                }
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-layer-3-header flex items-center justify-center text-text-secondary font-bold border border-border-default">
                {getAvatarInitial(
                  activeConversation.contactName ||
                    activeConversation.contactPhone,
                )}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm sm:text-[15px] font-semibold text-text-primary leading-tight truncate mb-0.5">
              {activeConversation.contactName ||
                activeConversation.contactPhone}
            </h3>
            <p className="text-[12px] sm:text-[13px] text-text-muted truncate max-w-full leading-tight">
              {activeConversation.contactPhone || 'Online'}
            </p>
          </div>
        </div>

        {/* Header Actions */}
        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          {/* Window Status Badge */}
          {isWindowActive ? (
            <div className="inline-flex items-center justify-center gap-[6px] px-2 sm:px-3 h-[33px] rounded-[10px] border border-primary bg-surface-mint text-primary text-[12px] sm:text-[14px] font-medium whitespace-nowrap">
              <span className="w-[8px] h-[8px] rounded-full bg-primary" />
              Active
            </div>
          ) : (
            <div className="inline-flex items-center justify-center gap-[6px] px-2 sm:px-3 h-[33px] rounded-[10px] border border-red-500 bg-red-50 text-red-500 text-[12px] sm:text-[14px] font-medium whitespace-nowrap">
              <span className="w-[8px] h-[8px] rounded-full bg-red-500" />
              Expired
            </div>
          )}

          {/* Refresh button */}
          {/* {onRefresh && (
            <button
              onClick={onRefresh}
              className="p-2 text-text-muted hover:text-text-primary rounded-button hover:bg-layer-7-hover transition-colors"
              title="Refresh Messages"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          )} */}
        </div>
      </div>

      {/* Expired Window Warning Banner */}
      {/* {!isWindowActive && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-amber-500/10 border-b border-amber-500/20">
          <svg
            className="w-4 h-4 text-amber-400 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <p className="text-xs text-amber-300">
            This WhatsApp conversation window is expired. You can view messages,
            but cannot send a new message.
            {expiredWindowLabel && (
              <span className="ml-1 opacity-70">
                (Expired: {expiredWindowLabel})
              </span>
            )}
          </p>
        </div>
      )} */}

      {/* Scrollable Message Area */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="wa-chat-thread flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden p-3 sm:p-4 custom-scrollbar relative"
      >
        <div ref={observerTargetRef} className="h-1 w-full" />

        {isFetchingNextPage && (
          <div className="flex justify-center mb-4">
            <span className="px-3 py-1 text-xs text-text-muted bg-input-background rounded-full border border-border-default">
              Loading older messages...
            </span>
          </div>
        )}

        {isLoading && !messages?.length ? (
          <div className="flex flex-col gap-4 p-4">
            <div className="flex justify-start">
              <SkeletonBox
                className="w-48 h-12"
                rounded="rounded-2xl rounded-tl-sm"
              />
            </div>
            <div className="flex justify-end">
              <SkeletonBox
                className="w-64 h-16"
                rounded="rounded-2xl rounded-tr-sm"
              />
            </div>
            <div className="flex justify-start">
              <SkeletonBox
                className="w-56 h-20"
                rounded="rounded-2xl rounded-tl-sm"
              />
            </div>
            <div className="flex justify-end">
              <SkeletonBox
                className="w-52 h-12"
                rounded="rounded-2xl rounded-tr-sm"
              />
            </div>
            <div className="flex justify-start">
              <SkeletonBox
                className="w-72 h-14"
                rounded="rounded-2xl rounded-tl-sm"
              />
            </div>
            <div className="flex justify-end">
              <SkeletonBox
                className="w-40 h-10"
                rounded="rounded-2xl rounded-tr-sm"
              />
            </div>
          </div>
        ) : messages?.length === 0 ? (
          <div className="flex h-full items-center justify-center text-text-muted text-sm">
            No messages yet. Send a message to start the conversation.
          </div>
        ) : (
          <div className="flex flex-col">
            {messages?.map((group, index) => (
              <div key={group.date || index} className="flex flex-col">
                {/* Date Divider */}
                <div className="flex justify-center my-4">
                  <span className="px-3 py-1 text-[11px] font-medium text-text-muted bg-input-background rounded-full border border-border-default">
                    {group.date}
                  </span>
                </div>

                {/* Messages for this date */}
                {group.messages?.map((msg, idx) => {
                  const nextMsg = group.messages[idx + 1];
                  // Hide the time if the next consecutive message is from the same direction and shares the same time string
                  const isLastInTimeBlock =
                    !nextMsg ||
                    nextMsg.direction !== msg.direction ||
                    nextMsg.time !== msg.time;

                  return (
                    <MessageBubble
                      key={msg.id}
                      message={msg}
                      isIncoming={msg.direction === 'inbound'}
                      onReact={onReactToMessage}
                      canReact={canReactMessages && isWindowActive}
                      showTime={isLastInTimeBlock}
                      onMediaReady={() => {
                        if (!isUserScrolledUpRef.current) {
                          messagesEndRef.current?.scrollIntoView({
                            behavior: 'auto',
                          });
                        }
                      }}
                      onMediaClick={(mediaMsg) => {
                        const list = mediaMsg.mediaList || allMedia;
                        setScopedViewerMedia(mediaMsg.mediaList || null);
                        const clickedIdx = list.findIndex(
                          (m) => m.id === mediaMsg.id,
                        );
                        setSelectedMediaIndex(clickedIdx >= 0 ? clickedIdx : 0);
                        setIsMediaViewerOpen(true);
                      }}
                    />
                  );
                })}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* New Message Alert */}
        {showNewMessageAlert && (
          <div className="sticky bottom-4 left-0 right-0 flex justify-center z-20 pointer-events-none">
            <button
              onClick={() => scrollToBottom('smooth')}
              className="pointer-events-auto flex items-center gap-2 px-4 py-2 bg-layer-3-header border border-border-default text-text-primary rounded-full shadow-lg hover:bg-layer-4-dropdown transition-colors text-sm font-medium animate-bounce"
            >
              <svg
                className="w-4 h-4 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
              New messages below
            </button>
          </div>
        )}
      </div>

      {/* Fixed Message Input */}
      <div
        className="wa-chat-composer shrink-0 z-30 p-2 sm:p-4 bg-layer-2-sidebar border-t border-border-default min-w-0 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
        style={keyboardInset ? { marginBottom: keyboardInset } : undefined}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          multiple
          accept={
            platform === 'instagram'
              ? 'image/*,video/*,audio/*'
              : 'image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv'
          }
          className="hidden"
        />
        {!canSendMessages ? (
          <div className="flex items-center justify-center gap-2 py-2 text-text-muted text-sm">
            <svg
              className="w-4 h-4 text-amber-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            <span>Messaging disabled — read-only permission</span>
          </div>
        ) : !isWindowActive ? (
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 px-3 py-2.5 text-center bg-[#FFF9E5] text-[#1F2241] text-[13px] font-medium leading-5 [overflow-wrap:anywhere]">
            <svg
              className="w-4 h-4 text-[#F59E0B] shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
              />
            </svg>
            <span className="min-w-0 max-w-full">
              Messaging Disabled Window Expired.
              {expiredWindowLabel && (
                <span className="ml-1 opacity-70">
                  (Expired: {expiredWindowLabel})
                </span>
              )}
            </span>
          </div>
        ) : isRecording ? (
          <div className="flex items-center justify-between w-full bg-[#F5F5F5] border border-gray-200 rounded-2xl px-4 py-2 text-sm shadow-inner">
            <div className="flex items-center gap-3">
              {/* Pulsing Recording Indicator */}
              <div className="relative flex items-center justify-center">
                <span className="w-3 h-3 rounded-full bg-red-500 animate-ping absolute opacity-75" />
                <span className="w-3 h-3 rounded-full bg-red-500 relative" />
              </div>

              {/* Dynamic Audio Waves Animation */}
              <div className="flex items-center gap-1 h-5 px-1">
                <span className="w-1 h-3 bg-red-500/80 rounded-full animate-[bounce_1s_infinite_100ms]" />
                <span className="w-1 h-5 bg-red-500 rounded-full animate-[bounce_1s_infinite_300ms]" />
                <span className="w-1 h-2 bg-red-500/70 rounded-full animate-[bounce_1s_infinite_200ms]" />
                <span className="w-1 h-4 bg-red-500/90 rounded-full animate-[bounce_1s_infinite_400ms]" />
                <span className="w-1 h-3 bg-red-500/80 rounded-full animate-[bounce_1s_infinite_150ms]" />
              </div>

              {/* Timer Display */}
              <span className="text-text-primary font-medium tracking-wide text-sm">
                {formatRecordingTime(recordingTime)}
              </span>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {/* Discard Recording Button */}
              <button
                type="button"
                onClick={cancelRecording}
                className="p-2 text-gray-500 hover:text-red-500 hover:bg-gray-200/60 rounded-full transition-colors"
                title="Discard recording"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>

              {/* Stop and Send Recording Button */}
              <button
                type="button"
                onClick={stopAndSendRecording}
                className="p-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full transition-all shadow-md hover:scale-105 active:scale-95 flex items-center justify-center"
                title="Send audio"
              >
                <svg
                  className="w-4 h-4 ml-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSend}
            className="flex items-end gap-2 sm:gap-3 min-w-0"
          >
            {/* Attachment Pin Button & Popup Menu */}
            <div className="relative shrink-0" ref={attachmentMenuRef}>
              <button
                type="button"
                onClick={() => setShowAttachmentMenu((prev) => !prev)}
                className="bg-[#F5F5F5] text-text-muted hover:text-text-primary rounded-xl transition-colors shrink-0"
                title="Attach file"
              >
                <img
                  src={AttachmentClip}
                  alt="Attach"
                  className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
                />
              </button>

              {/* Attachment Popup Menu (Ultra-Compact Fit Content) */}
              {showAttachmentMenu && (
                <div className="absolute bottom-full mb-2 left-0 z-[100] w-max p-1 bg-white rounded-2xl shadow-2xl border border-gray-100/90 flex flex-col gap-0.5 select-none animate-fadeIn">
                  {/* Document Option (WhatsApp only) */}
                  {platform !== 'instagram' && (
                    <button
                      type="button"
                      onClick={() =>
                        triggerFileInput(
                          '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar',
                        )
                      }
                      className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-[#F5F5F5] transition-colors w-full text-left group"
                    >
                      <div className="w-7 h-7 rounded-lg bg-[#7c3aed]/10 text-[#7c3aed] flex items-center justify-center shrink-0">
                        <svg
                          className="w-3.5 h-3.5"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
                        </svg>
                      </div>
                      <span className="text-xs font-semibold text-gray-900 leading-tight">
                        Document
                      </span>
                    </button>
                  )}

                  {/* Photos & Videos Option */}
                  <button
                    type="button"
                    onClick={() => triggerFileInput('image/*,video/*')}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-[#F5F5F5] transition-colors w-full text-left group"
                  >
                    <div className="w-7 h-7 rounded-lg bg-[#0284c7]/10 text-[#0284c7] flex items-center justify-center shrink-0">
                      <svg
                        className="w-3.5 h-3.5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                      </svg>
                    </div>
                    <span className="text-xs font-semibold text-gray-900 leading-tight whitespace-nowrap">
                      Photos &amp; videos
                    </span>
                  </button>

                  {/* Audio Option */}
                  <button
                    type="button"
                    onClick={() =>
                      triggerFileInput(
                        platform === 'instagram'
                          ? 'audio/mp3,audio/mpeg,audio/m4a,audio/aac,audio/wav,.mp3,.m4a,.aac,.wav'
                          : 'audio/*',
                      )
                    }
                    className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-[#F5F5F5] transition-colors w-full text-left group"
                  >
                    <div className="w-7 h-7 rounded-lg bg-[#ea580c]/10 text-[#ea580c] flex items-center justify-center shrink-0">
                      <svg
                        className="w-3.5 h-3.5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
                      </svg>
                    </div>
                    <span className="text-xs font-semibold text-gray-900 leading-tight">
                      Audio
                    </span>
                  </button>
                </div>
              )}
            </div>

            {/* Main Input Container */}
            <div className="flex-1 min-w-0 relative flex items-end bg-[#F5F5F5] rounded-2xl px-2 sm:px-4 py-1.5">
              {showFormatBar && (
                <div
                  className="absolute bottom-full left-2 mb-2 z-[90] flex items-center gap-0.5 rounded-lg border border-border-default bg-layer-3-header px-1 py-1 shadow-md"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <button
                    type="button"
                    title="Bold (Ctrl+B)"
                    onClick={() => applyFormat('bold')}
                    className="px-2 py-1 text-sm font-semibold text-text-primary rounded hover:bg-layer-7-hover"
                  >
                    B
                  </button>
                  <button
                    type="button"
                    title="Italic (Ctrl+I)"
                    onClick={() => applyFormat('italic')}
                    className="px-2 py-1 text-sm italic text-text-primary rounded hover:bg-layer-7-hover"
                  >
                    I
                  </button>
                  <button
                    type="button"
                    title="Underline (Ctrl+U)"
                    onClick={() => applyFormat('underline')}
                    className="px-2 py-1 text-sm underline text-text-primary rounded hover:bg-layer-7-hover"
                  >
                    U
                  </button>
                  <button
                    type="button"
                    title="Strikethrough (Ctrl+Shift+X)"
                    onClick={() => applyFormat('strikeThrough')}
                    className="px-2 py-1 text-sm line-through text-text-primary rounded hover:bg-layer-7-hover"
                  >
                    S
                  </button>
                </div>
              )}
              {!hasDraft && (
                <span className="pointer-events-none absolute left-2 sm:left-4 top-1.5 py-2 text-[15px] text-text-muted">
                  Type a message
                </span>
              )}
              <div
                ref={messageInputRef}
                id="chat-message-input"
                contentEditable
                role="textbox"
                aria-multiline="true"
                onInput={syncDraft}
                onKeyDown={handleMessageKeyDown}
                onPaste={handleComposerPaste}
                className="relative z-[1] flex-1 min-w-0 min-h-[36px] max-h-32 overflow-x-hidden overflow-y-auto whitespace-pre-wrap break-words bg-transparent text-text-primary text-[15px] leading-[1.45] py-2 focus:outline-none [overflow-wrap:anywhere]"
              />

              <div className="flex items-center gap-1.5 ml-2">
                {/* Emoji Button */}
                <div className="relative shrink-0" ref={emojiPickerRef}>
                  <button
                    type="button"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="p-1.5 text-text-muted hover:text-text-primary rounded-full transition-colors"
                    title="Emojis"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </button>

                  {showEmojiPicker && (
                    <div className="absolute bottom-12 right-0 z-[100] shadow-2xl">
                      <LazyEmojiPicker
                        theme="light"
                        onEmojiSelect={(emoji) =>
                          handleEmojiSelect(emoji.native)
                        }
                        previewPosition="none"
                        skinTonePosition="none"
                        width="320px"
                        height="350px"
                      />
                    </div>
                  )}
                </div>

                {/* Audio Record Button */}
                <button
                  type="button"
                  onClick={startRecording}
                  className="p-1.5 text-text-muted hover:text-text-primary rounded-full transition-colors shrink-0"
                  title="Record audio message"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Send Button */}
            <button
              type="submit"
              disabled={!hasDraft}
              className="bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            >
              <img
                src={SendMsgIcon}
                alt="Send"
                className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
              />
            </button>
          </form>
        )}
      </div>

      {/* WhatsApp-style Fullscreen Media Viewer Modal */}
      <MediaViewerModal
        isOpen={isMediaViewerOpen}
        onClose={() => {
          setIsMediaViewerOpen(false);
          setScopedViewerMedia(null);
        }}
        mediaList={scopedViewerMedia ?? allMedia}
        initialIndex={selectedMediaIndex}
        contactName={
          activeConversation?.contactName || activeConversation?.contactPhone
        }
        surface={platform === 'whatsapp' ? 'whatsapp-inbox' : ''}
      />
    </div>
  );
}
